import BlankCellViewer from '@/components/dataGrid/components/renderers/cell/view/blankCellViewer.vue';
import AddColumnHeader from '@/components/dataGrid/components/renderers/headers/AddColumnHeader.vue';
import Helpers from '@/helpers/index.js';
import ProjectHelpers from '@/helpers/projects.helpers.ts';
import { API } from '@/services';
import { UPDATE_CREATOR_CONTRACT_MUTATION } from '@/services/project/mutations/contract/index.js';
import {
  PROJECT_CREATOR_SYNC_BRIEFINGFILES_MUTATION,
  PROJECT_CREATOR_SYNC_PRODUCTS_MUTATION,
  REMOVE_PROJECT_CREATOR_MUTATION,
  UPDATE_CREATOR_MUTATION,
  UPDATE_PROJECT_CREATOR_MUTATION,
} from '@/services/project/mutations/creator/index.js';
import { UPDATE_PROJECT_VIEWS_MUTATION } from '@/services/project/mutations/project/index.js';
import { GET_PROJECT_CREATORS_QUERY, GET_PROJECT_ENTITY_MAPPING_QUERY } from '@/services/project/queries/index.js';
import { useProjectStore } from '@/stores/project/index.store.ts';
import { defineStore } from 'pinia';

export const useProjectDetailsStore = defineStore({
  id: 'projectDetails',
  state: () => ({
    showGrid: true,
    isLoadingCreators: false,
    creators: [],
    filteredSortedCreators: [],
    activeCreators: false,
    filteredActiveCreators: [],
    views: [],
    filterOptions: [],
    activeView: {
      name: 'Internal Sheet',
      base: 'creator_details',
      canEdit: false,
      shared: false,
      displayMode: 'grid',
      grouping: [],
      sort: [],
      filter: null,
      hidden_fields: [],
      column_state: [],
      custom_properties: [{ key: 'Last Used', value: 'true' }],
    },
    tempUploadedData: {
      creators: [],
      entity_mapping: [],
      lockedCustomColumns: [],
      originalCreators: [],
    },
    entityMappings: [],
    tempLockedCustomColumns: [],
  }),
  getters: {
    products(state) {
      return useProjectStore().products;
    },
    briefing_files(state) {
      return useProjectStore().briefFiles;
    },
    cardDetails(state) {
      return state.filteredSortedCreators;
    },
    columns(state) {
      // generate columns based on the project's entity mappings'
      const customColumns = state.entityMappings.map((mapping) => ProjectHelpers.formatCustomColumn(mapping));

      const addColumn = {
        headerName: 'addColumn',
        headerComponent: AddColumnHeader,
        cellRenderer: BlankCellViewer,
        field: 'addColumn',
        width: 150,
        sort: false,
        filter: false,
        suppressHeaderMenuButton: true, // Disable any context menu on this column
        suppressMovable: true, // Prevent this column from being moved
        suppressColumnsToolPanel: true, // Hide the column in the columns panel
        lockPosition: 'right',
        suppressSizeToFit: true,
        suppressHeaderContextMenu: true,
      };

      let data = [
        {
          field: 'username.display_value',
          headerName: 'Name',
          headerClass: 'header-user',
          sortable: true,
          editable: true,
          filter: 'agSetColumnFilter',
          filterParams: {
            defaultToNothingSelected: true,
          },
          // filter: true,
          // filterParams: {
          //   defaultToNothingSelected: true,
          //   suppressMultiSelect: true
          //   // suppressSetFilter: true // Disables multi-select on filter
          //   // defaultOption: 'equals', // Default condition
          //   // filterOptions: ['equals'], // Lock to "Equals" only
          //   // alwaysShowBothConditions: false // Hide additional condition dropdown
          // },
          cellEditorParams: {
            key: 'creator',
          },
          context: {
            renderViewType: 'user',
            renderEditType: 'user',
          },
          suppressKeyboardEvent: (params) => {
            const key = params.event.key;
            if (key === 'Delete') {
              // Prevent the deletion of the row

              params.event.preventDefault();
              return true;
            }
          },
        },
        {
          field: 'confirmed.display_value',
          cellRenderer: 'agCheckboxCellRenderer',
          cellEditor: 'agCheckboxCellEditor',
          headerClass: 'header-checkbox',
          headerName: 'Creator Confirmed',
          singleClickEdit: true,
          sortable: true,
          editable: true,
          filter: true,
          keyCreator: (params) => (params.value ? 'Yes' : 'No'),
          filterParams: {
            valueFormatter: (params) => params.value,
            defaultToNothingSelected: true,
            values: ['Yes', 'No'],
          },
          filterValueGetter: (params) => {
            return params.data.confirmed.display_value ? 'Yes' : 'No';
          },
          cellEditorParams: {
            key: 'confirmed',
          },
          context: {},
        },
        {
          field: 'initial_quote.display_value',
          cellRenderer: (params) => {
            return params.data?.initial_quote.display_value ? `$${Helpers.formatNumberWithCommas(Number(params.data.initial_quote.display_value))}` : '';
          },
          keyCreator: (params) => (params.value ? `$${Helpers.formatNumberWithCommas(Number(params.value))}` : '$0'),
          headerName: 'Initial Quote',
          headerClass: 'header-currency',
          singleClickEdit: true,
          sortable: true,
          editable: true,
          cellDataType: 'number',
          filter: 'agNumberColumnFilter',
          filterParams: {
            defaultToNothingSelected: true,
            valueFormatter: (params) => params.value ?? 0,
          },
          cellEditorParams: {
            key: 'initial_quote',
            precision: 2,
            step: 0.01,
            showStepperButtons: true,
          },
          context: {
            filterType: 'currency',
            renderEditType: 'currency',
            payloadTransformer: (payload) => {
              return Number(payload);
            },
          },
        },
        {
          field: 'contract_value.display_value',
          cellRenderer: (params) => {
            return params.data?.contract_value.display_value ? `$${Helpers.formatNumberWithCommas(Number(params.data.contract_value.display_value))}` : '';
          },
          keyCreator: (params) => (params.value ? `$${Helpers.formatNumberWithCommas(Number(params.value))}` : '$0'),
          headerName: 'Final Rate',
          headerClass: 'header-currency',
          singleClickEdit: true,
          cellDataType: 'number',
          sortable: true,
          editable: true,
          filter: 'agNumberColumnFilter',
          filterParams: {
            valueFormatter: (params) => params.value ?? 0,
          },
          cellEditorParams: {
            key: 'contract_value',
            precision: 2,
            step: 0.01,
            showStepperButtons: true,
          },
          context: {
            filterType: 'currency',
            renderEditType: 'currency',
            payloadTransformer: (payload) => {
              return Number(payload);
            },
          },
        },
        {
          field: 'savings.display_value',
          cellRenderer: (params) => {
            if (params.data?.initial_quote.display_value && params.data?.contract_value.display_value) {
              const savings = Number(params.data.initial_quote.display_value) - Number(params.data.contract_value.display_value);
              // Round to 2 decimal places
              const rounded = Math.round(savings * 100) / 100;
              return `$${Helpers.formatNumberWithCommas(rounded)}`;
            }
            return '';
          },
          valueGetter: (params) => {
            if (params.data?.initial_quote.display_value && params.data?.contract_value.display_value) {
              const savings = Number(params.data.initial_quote.display_value) - Number(params.data.contract_value.display_value);
              // Round to 2 decimal places
              const rounded = Math.round(savings * 100) / 100;
              return `$${Helpers.formatNumberWithCommas(rounded)}`;
            }
            return '$0';
          },
          headerName: 'Savings',
          headerClass: 'header-formula',
          sortable: true,
          editable: false,
          filter: 'agNumberColumnFilter',
          filterParams: {
            valueFormatter: (params) => params.value ?? 0,
          },
          cellEditorParams: {
            key: 'savings',
          },
          context: {
            turnOffEndpoint: true,
            filterType: 'currency',
            renderViewType: 'custom',
          },
        },
        {
          field: 'favorite.display_value',
          keyCreator: (params) => {
            // Used for grouping - returns the same values as filterValueGetter
            const favoriteValue = params.data?.favorite?.display_value;
            return favoriteValue === true ? 'Recommended' : 'Others';
          },
          headerName: 'Favs',
          headerClass: 'header-favorite',
          sortable: true,
          editable: true,
          filter: 'agSetColumnFilter',
          singleClickEdit: true,
          filterParams: {
            defaultToNothingSelected: true,
            valueFormatter: (params) => params.value,
            // Don't specify values array - let AG Grid derive from filterValueGetter
            // This avoids the "Key Creator with primitives" error
          },
          filterValueGetter: (params) => {
            // Access the favorite value directly from the data structure
            // The field is 'favorite.display_value', so we need to check params.data.favorite.display_value
            const favoriteValue = params.data?.favorite?.display_value;
            // Check if favorite is true (boolean) - anything else (false, null, undefined, '') is 'Others'
            // Must return the same values as keyCreator for grouping to work correctly
            return favoriteValue === true ? 'Recommended' : 'Others';
          },
          cellEditorParams: {
            key: 'favorite',
          },
          context: {
            renderViewType: 'favorite',
            renderEditType: 'favorite',
          },
        },
        {
          field: 'notes.display_value',
          headerName: 'Summary',
          headerClass: 'header-string',
          sortable: true,
          editable: true,
          filter: true,
          keyCreator: (params) => (params.value ? 'Yes' : 'No'),
          filterParams: {
            valueFormatter: (params) => params.value,
            defaultToNothingSelected: true,
            values: ['Yes', 'No'],
          },
          filterValueGetter: (params) => {
            return params.data.notes.display_value ? 'Yes' : 'No';
          },
          cellEditorParams: {
            key: 'notes',
            maxLength: 10000,
          },
          cellEditor: 'agLargeTextCellEditor',
          context: {},
        },
        {
          field: 'contract_status.display_value',
          headerName: 'Contract Status',
          headerClass: 'header-dropdown',
          sortable: true,
          editable: true,
          filter: true,
          filterParams: {
            defaultToNothingSelected: true,
            values: ProjectHelpers.getClientStatusOptions().map((status) => status.value),
          },
          cellEditorParams: {
            key: 'contract_status',
          },
          context: {
            renderViewType: 'chip',
            renderEditType: 'single-select',
            dropdownSelections: ProjectHelpers.getClientStatusOptions(),
          },
        },
        {
          field: 'phone.display_value',
          keyCreator: (params) => (params.value ? 'Yes' : 'No'),
          headerName: 'Phone #',
          headerClass: 'header-string',
          sortable: true,
          editable: true,
          filter: true,
          filterParams: {
            valueFormatter: (params) => params.value,
            defaultToNothingSelected: true,
            values: ['Yes', 'No'],
          },
          filterValueGetter: (params) => {
            return params.data.phone.display_value ? 'Yes' : 'No';
          },
          cellEditorParams: {
            key: 'phone',
          },
          cellRenderer: (params) => {
            return params.data?.phone.display_value ?? '';
          },
          context: {},
        },
        {
          field: 'email.display_value',
          headerName: 'Contact Email',
          headerClass: 'header-string',
          sortable: true,
          editable: true,
          keyCreator: (params) => (params.value ? 'Yes' : 'No'),
          filter: true,
          filterParams: {
            defaultToNothingSelected: true,
            valueFormatter: (params) => params.value,
            values: ['Yes', 'No'],
          },
          filterValueGetter: (params) => {
            return params.data.email.display_value ? 'Yes' : 'No';
          },
          cellEditorParams: {
            key: 'email',
          },
          cellRenderer: (params) => {
            return params.data?.email.display_value ? `<a href="mailto:${params.data.email.display_value}" target="_blank">${params.data.email.display_value}</a>` : '';
          },
          context: {},
        },
        {
          field: 'social_channels.display_value',
          headerName: 'Handles',
          headerClass: 'header-web',
          sortable: true,
          editable: false,
          filter: true,
          suppressPaste: true,
          suppressFillHandle: true,
          keyCreator: ({ value }) => {
            if (Array.isArray(value)) {
              return value.map((b) => b.type ?? '').join(', ');
            }
            return value ?? '';
          },
          filterParams: {
            defaultToNothingSelected: true,
            values: ProjectHelpers.getPostingToSocialChannelsOptions().map((posting) => posting?.value),
            valueFormatter: (params) => {
              return params.data;
            },
            keyCreator: (params) => {
              return params.value || [];
            },
          },
          filterValueGetter: (params) => {
            return params.data.social_channels.display_value.map((social) => social?.type);
          },
          cellEditorParams: {
            key: 'social_channels',
          },
          context: {
            customField: true,
            renderViewType: 'social_channels',
            renderEditType: 'social_channels',
          },
          suppressKeyboardEvent: (params) => {
            const key = params.event.key;
            if (key === 'Delete') {
              // Prevent the deletion of the row
              alert('Delete action is not supported for social channels, please select a social channel to delete it.');

              params.event.preventDefault();
              return true;
            }
          },
        },
        {
          field: 'deliverables.display_value',
          keyCreator: (params) => {
            return params.value.length > 0 ? 'Yes' : 'No';
          },
          headerName: 'Deliverables',
          headerClass: 'header-dropdown',
          sortable: true,
          editable: false,
          filter: true,
          suppressPaste: false,
          suppressFillHandle: false,
          filterParams: {
            defaultToNothingSelected: true,
            valueFormatter: (params) => params.value,
            values: ['Yes', 'No'],
          },
          filterValueGetter: (params) => {
            return params.data.deliverables.display_value.length > 0 ? 'Yes' : 'No';
          },
          cellEditorParams: {
            key: 'deliverables',
          },
          context: {
            turnOffEndpoint: true, // this prevents the use of the endpoint to fetch data (turn this off until a sync endpoint is implemented)
            renderViewType: 'deliverables',
            renderEditType: 'deliverables',
            multiSelect: true,
            returnObject: true,
            dropdownSelections: [],
            // payloadTransformer: (payload) => {
            //   return payload ? payload.map((deliverable) => Number(deliverable.id)) : [];
            // },
          },
          suppressKeyboardEvent: (params) => {
            const key = params.event.key;
            if (key === 'Delete') {
              // Prevent the deletion of the row
              params.event.preventDefault();
              alert('Delete action is not supported for deliverables, please select a deliverable to delete it.');
              return true;
            }
          },
        },
        {
          field: 'products.display_value',
          keyCreator: ({ value }) => {
            if (Array.isArray(value)) {
              return value.map((b) => b.name ?? '').join(', ');
            }
            return value ?? '';
          },
          headerName: 'Products',
          headerClass: 'header-dropdown',
          sortable: true,
          editable: true,
          filter: true,
          filterParams: {
            defaultToNothingSelected: true,
            valueFormatter: (params) => params.value,
            values: state.products.map((product) => product.name),
          },
          filterValueGetter: (params) => {
            return params.data.products.display_value.map((products) => products.name);
          },
          cellEditorParams: {
            key: 'products',
          },
          context: {
            renderViewType: 'products',
            renderEditType: 'products',
            multiSelect: true,
            returnObject: true,
            dropdownSelections: state.products,
            payloadTransformer: (payload) => {
              return payload ? payload.map((product) => Number(product.id)) : [];
            },
          },
        },
        // {
        //   field: 'documents.display_value',
        //   headerName: 'Documents',
        //   headerClass: 'header-string',
        //   sortable: true,
        //   editable: false,
        //   keyCreator: (params) => (params.value ? 'Yes' : 'No'),
        //   filter: true,
        //   filterParams: {
        //     defaultToNothingSelected: true,
        //     keyCreator: (params) => params.value,
        //     valueFormatter: (params) => params.value,
        //     values: ['Yes', 'No'],
        //   },
        //   filterValueGetter: (params) => {
        //     return params.data.documents.display_value ? 'Yes' : 'No';
        //   },
        //   cellEditorParams: {
        //     key: 'documents',
        //   },
        //   context: {},
        // },
        {
          field: 'documents.display_value',
          keyCreator: (params) => {
            return params.value.length > 0 ? 'Yes' : 'No';
          },
          headerName: 'Documents',
          headerClass: 'header-file',
          sortable: true,
          editable: true,
          filter: true,
          suppressPaste: false,
          suppressFillHandle: false,
          filterParams: {
            defaultToNothingSelected: true,
            valueFormatter: (params) => params.value,
            values: ['Yes', 'No'],
          },
          filterValueGetter: (params) => {
            return params.data.documents.display_value.length > 0 ? 'Yes' : 'No';
          },
          cellEditorParams: {
            key: 'documents',
          },
          context: {
            turnOffEndpoint: true, // this prevents the use of the endpoint to fetch data (turn this off until a sync endpoint is implemented)
            renderViewType: 'documents',
            renderEditType: 'documents',
            multiSelect: true,
            returnObject: true,
            dropdownSelections: [],
            // payloadTransformer: (payload) => {
            //   return payload ? payload.map((deliverable) => Number(deliverable.id)) : [];
            // },
          },
          suppressKeyboardEvent: (params) => {
            const key = params.event.key;
            if (key === 'Delete') {
              // Prevent the deletion of the row
              params.event.preventDefault();
              alert('Delete action is not supported for documents, please select a document to delete it.');
              return true;
            }
          },
        },
        {
          field: 'briefing_files.display_value',
          headerName: 'Briefing Files',
          headerClass: 'header-dropdown',
          sortable: true,
          editable: true,
          filter: true,
          valueFormatter: (params) => {
            const list = params.data?.briefing_files?.display_value ?? [];
            const src = params.colDef?.context?.dropdownSelections ?? [];
            const byId = Object.create(null);
            for (const f of src) {
              byId[Number(f.id ?? f?.raw?.id)] = f;
            }

            const nameOf = (v) => {
              if (typeof v === 'object') return v.name ?? v.filename ?? v.raw?.name ?? v.raw?.filename ?? '';
              const hit = byId[Number(v)];
              return hit?.filename ?? hit?.name ?? hit?.raw?.filename ?? hit?.raw?.name ?? '';
            };
            return Array.isArray(list) ? list.map(nameOf).filter(Boolean).join(', ') : '';
          },
          keyCreator: ({ value }) =>
            Array.isArray(value)
              ? value.map((v) => (typeof v === 'object' ? (v.name ?? v.filename ?? v.raw?.name ?? v.raw?.filename ?? String(v.id)) : String(v))).join(', ')
              : (value ?? ''),

          filterParams: {
            defaultToNothingSelected: true,
            valueFormatter: (p) => p.value,
            values: state.briefing_files.map((b) => b.filename),
          },
          filterValueGetter: (params) => {
            const list = params.data?.briefing_files?.display_value ?? [];
            return Array.isArray(list)
              ? list.map((v) =>
                  typeof v === 'object' ? (v.filename ?? v.name ?? v.raw?.filename ?? v.raw?.name ?? '') : (state.briefing_files.find((b) => Number(b.id) === Number(v))?.filename ?? '')
                )
              : [];
          },
          cellEditorParams: { key: 'briefing_files' },
          context: {
            renderViewType: 'briefing_files',
            renderEditType: 'briefing_files',
            multiSelect: true,
            returnObject: true,
            dropdownSelections: state.briefing_files,
            isDeliverables: false,
            payloadTransformer: (payload) => (Array.isArray(payload) ? payload.map((v) => (typeof v === 'object' ? Number(v?.id ?? v?.raw?.id) : Number(v))).filter(Boolean) : []),
          },
        },
        {
          field: 'example_content.display_value',
          cellEditor: 'agLargeTextCellEditor',
          headerName: 'Example Content',
          headerClass: 'header-url',
          sortable: true,
          editable: true,
          keyCreator: (params) => (params.value ? 'Yes' : 'No'),
          filter: true,
          filterParams: {
            defaultToNothingSelected: true,
            valueFormatter: (params) => params.value,
            values: ['Yes', 'No'],
          },
          filterValueGetter: (params) => {
            return params.data.example_content.display_value ? 'Yes' : 'No';
          },
          cellEditorParams: {
            key: 'example_content',
            maxLength: 10000,
          },
          context: {
            renderViewType: 'example_content',
          },
        },
        {
          field: 'instagram_reach.display_value',
          headerName: 'IG Reach',
          headerClass: 'header-instagram',
          cellRenderer: (params) => {
            return params.data?.instagram_reach?.display_value && params.data?.instagram_reach.display_value !== 0
              ? Helpers.formatNumberWithCommas(Number(params.data?.instagram_reach.display_value))
              : '';
          },
          keyCreator: (params) => (params.value ? Helpers.formatNumberWithCommas(Number(params.value)) : ''),
          sortable: true,
          editable: true,
          cellDataType: 'number',
          filter: 'agNumberColumnFilter',
          singleClickEdit: true,
          filterParams: {
            defaultToNothingSelected: true,
            valueFormatter: (params) => params.value ?? 0,
          },
          cellEditorParams: {
            key: 'instagram_reach',
            precision: 0,
            step: 1,
            showStepperButtons: true,
          },
          context: {
            filterType: 'number',
            renderEditType: 'integer',
            payloadTransformer: (payload) => {
              return Number(payload);
            },
          },
        },
        {
          field: 'instagram_impressions.display_value',
          headerName: 'IG Impressions',
          headerClass: 'header-instagram',
          cellRenderer: (params) => {
            return params.data?.instagram_impressions?.display_value && params.data?.instagram_impressions.display_value !== 0
              ? Helpers.formatNumberWithCommas(Number(params.data?.instagram_impressions.display_value))
              : '';
          },
          keyCreator: (params) => (params.value ? Helpers.formatNumberWithCommas(Number(params.value)) : ''),
          sortable: true,
          editable: true,
          cellDataType: 'number',
          filter: 'agNumberColumnFilter',
          singleClickEdit: true,
          filterParams: {
            defaultToNothingSelected: true,
            valueFormatter: (params) => params.value ?? 0,
          },
          cellEditorParams: {
            key: 'instagram_impressions',
            precision: 0,
            showStepperButtons: true,
          },
          context: {
            filterType: 'number',
            renderEditType: 'integer',
            payloadTransformer: (payload) => {
              return Number(payload);
            },
          },
        },
        {
          field: 'instagram_engagement_rate.display_value',
          headerName: 'IG ER',
          headerClass: 'header-instagram',
          cellRenderer: (params) => {
            return params.data?.instagram_engagement_rate?.display_value && params.data?.instagram_engagement_rate.display_value !== 0
              ? `${params.data?.instagram_engagement_rate?.display_value}%`
              : '';
          },
          keyCreator: (params) => (params.value ? `${params.value}%` : ''),
          sortable: true,
          editable: true,
          cellDataType: 'number',
          filter: 'agNumberColumnFilter',
          singleClickEdit: true,
          filterParams: {
            defaultToNothingSelected: true,
            valueFormatter: (params) => params.value ?? 0,
          },
          cellEditorParams: {
            key: 'instagram_engagement_rate',
            precision: 2,
            showStepperButtons: true,
          },
          context: {
            filterType: 'number',
            renderEditType: 'integer',
            payloadTransformer: (payload) => {
              return Number(payload);
            },
          },
        },
        {
          field: 'tiktok_reach.display_value',
          headerClass: 'header-tiktok',
          headerName: 'TT Reach',
          cellRenderer: (params) => {
            return params.data?.tiktok_reach?.display_value && params.data?.tiktok_reach.display_value !== 0
              ? Helpers.formatNumberWithCommas(Number(params.data?.tiktok_reach.display_value))
              : '';
          },
          keyCreator: (params) => (params.value ? Helpers.formatNumberWithCommas(Number(params.value)) : ''),
          sortable: true,
          editable: true,
          cellDataType: 'number',
          filter: 'agNumberColumnFilter',
          singleClickEdit: true,
          filterParams: {
            defaultToNothingSelected: true,
            valueFormatter: (params) => params.value ?? 0,
          },
          cellEditorParams: {
            key: 'tiktok_reach',
            precision: 0,
            showStepperButtons: true,
          },
          context: {
            filterType: 'number',
            renderEditType: 'integer',
            payloadTransformer: (payload) => {
              return Number(payload);
            },
          },
        },
        {
          field: 'tiktok_impressions.display_value',
          headerClass: 'header-tiktok',
          headerName: 'TT Impressions',
          cellRenderer: (params) => {
            return params.data?.tiktok_impressions?.display_value && params.data?.tiktok_impressions.display_value !== 0
              ? Helpers.formatNumberWithCommas(Number(params.data?.tiktok_impressions.display_value))
              : '';
          },
          keyCreator: (params) => (params.value ? Helpers.formatNumberWithCommas(Number(params.value)) : ''),
          sortable: true,
          editable: true,
          cellDataType: 'number',
          filter: 'agNumberColumnFilter',
          singleClickEdit: true,
          filterParams: {
            defaultToNothingSelected: true,
            valueFormatter: (params) => params.value ?? 0,
          },
          cellEditorParams: {
            key: 'tiktok_impressions',
            precision: 0,
            showStepperButtons: true,
          },
          context: {
            filterType: 'number',
            renderEditType: 'integer',
            payloadTransformer: (payload) => {
              return Number(payload);
            },
          },
        },
        {
          field: 'tiktok_engagement_rate.display_value',
          headerClass: 'header-tiktok',
          headerName: 'TT ER',
          cellRenderer: (params) => {
            return params.data?.tiktok_engagement_rate?.display_value && params.data?.tiktok_engagement_rate.display_value !== 0
              ? `${params.data?.tiktok_engagement_rate?.display_value}%`
              : '';
          },
          keyCreator: (params) => (params.value ? `${params.value}%` : ''),
          sortable: true,
          editable: true,
          cellDataType: 'number',
          filter: 'agNumberColumnFilter',
          singleClickEdit: true,
          filterParams: {
            defaultToNothingSelected: true,
            valueFormatter: (params) => params.value ?? 0,
          },
          cellEditorParams: {
            key: 'tiktok_engagement_rate',
            precision: 2,
            showStepperButtons: true,
          },
          context: {
            filterType: 'number',
            payloadTransformer: (payload) => {
              return Number(payload);
            },
          },
        },
        {
          field: 'youtube_reach.display_value',
          headerClass: 'header-youtube',
          headerName: 'YT Reach',
          cellRenderer: (params) => {
            return params.data?.youtube_reach.display_value && params.data?.youtube_reach.display_value !== 0
              ? Helpers.formatNumberWithCommas(Number(params.data?.youtube_reach.display_value))
              : '';
          },
          keyCreator: (params) => (params.value ? Helpers.formatNumberWithCommas(Number(params.value)) : ''),
          sortable: true,
          editable: true,
          cellDataType: 'number',
          filter: 'agNumberColumnFilter',
          singleClickEdit: true,
          filterParams: {
            defaultToNothingSelected: true,
            valueFormatter: (params) => params.value ?? 0,
          },
          cellEditorParams: {
            key: 'youtube_reach',
            precision: 0,
            showStepperButtons: true,
          },
          context: {
            filterType: 'number',
            payloadTransformer: (payload) => {
              return Number(payload);
            },
          },
        },
        {
          field: 'youtube_impressions.display_value',
          headerName: 'YT Impressions',
          headerClass: 'header-youtube',
          cellRenderer: (params) => {
            return params.data?.youtube_impressions.display_value && params.data?.youtube_impressions.display_value !== 0
              ? Helpers.formatNumberWithSuffix(Number(params.data?.youtube_impressions.display_value))
              : '';
          },
          sortable: true,
          editable: true,
          cellDataType: 'number',
          filter: 'agNumberColumnFilter',
          singleClickEdit: true,
          filterParams: {
            defaultToNothingSelected: true,
            valueFormatter: (params) => params.value ?? 0,
          },
          cellEditorParams: {
            key: 'youtube_impressions',
            precision: 0,
            showStepperButtons: true,
          },
          context: {
            filterType: 'number',
            renderEditType: 'integer',
            payloadTransformer: (payload) => {
              return Number(payload);
            },
          },
        },
        {
          field: 'youtube_engagement_rate.display_value',
          headerName: 'YT ER',
          headerClass: 'header-youtube',
          cellRenderer: (params) => {
            return params.data?.youtube_engagement_rate?.display_value && params.data?.youtube_engagement_rate.display_value !== 0
              ? `${params.data?.youtube_engagement_rate?.display_value}%`
              : '';
          },
          keyCreator: (params) => (params.value ? `${params.value}%` : ''),
          sortable: true,
          editable: true,
          cellDataType: 'number',
          filter: 'agNumberColumnFilter',
          singleClickEdit: true,
          filterParams: {
            defaultToNothingSelected: true,
            valueFormatter: (params) => params.value ?? 0,
          },
          cellEditorParams: {
            key: 'youtube_engagement_rate',
            precision: 2,
            showStepperButtons: true,
          },
          context: {
            filterType: 'number',
            payloadTransformer: (payload) => {
              return Number(payload);
            },
          },
        },
        {
          field: 'total_reach',
          headerName: 'Total Reach',
          headerClass: 'header-formula',
          cellRenderer: (params) => {
            // add instagram, tiktok, and youtube reach
            const reach = Number(params.data?.instagram_reach.display_value) + Number(params.data?.tiktok_reach.display_value) + Number(params.data?.youtube_reach.display_value);
            return reach ? Helpers.formatNumberWithCommas(Number(reach)) : '';
          },
          valueGetter: (params) => {
            const reach = Number(params.data?.instagram_reach.display_value) + Number(params.data?.tiktok_reach.display_value) + Number(params.data?.youtube_reach.display_value);
            return reach ? Helpers.formatNumberWithCommas(Number(reach)) : '';
          },
          sortable: true,
          editable: false,
          filter: 'agNumberColumnFilter',
          filterParams: {
            defaultToNothingSelected: true,
            valueFormatter: (params) => params.value ?? 0,
          },
          cellEditorParams: {
            key: 'total_reach',
          },
          context: {
            turnOffEndpoint: true,
            filterType: 'number',
            // renderViewType: 'custom'
          },
        },
        {
          field: 'total_estimated_impressions',
          headerClass: 'header-formula',
          headerName: 'Total Est. Impressions',
          cellRenderer: (params) => {
            // add instagram, tiktok, and youtube impressions
            const impressions =
              Number(params.data?.instagram_impressions.display_value) + Number(params.data?.tiktok_impressions.display_value) + Number(params.data?.youtube_impressions.display_value);
            return impressions ? Helpers.formatNumberWithCommas(Number(impressions)) : '';
          },
          sortable: true,
          editable: false,
          filter: true,
          filterParams: {
            defaultToNothingSelected: true,
          },
          cellEditorParams: {
            key: 'total_estimated_impressions',
          },
          context: {
            turnOffEndpoint: true,
            renderViewType: 'custom',
          },
        },
        {
          field: 'verticals.display_value',
          headerName: 'Verticals',
          headerClass: 'header-dropdown',
          sortable: true,
          editable: true,
          filter: true,
          filterParams: {
            defaultToNothingSelected: true,
            values: ProjectHelpers.getVerticalOptions().map((option) => option?.value),
          },
          cellEditorParams: {
            key: 'verticals',
          },
          context: {
            renderViewType: 'chip',
            renderEditType: 'single-select',
            multiSelect: true,
            dropdownSelections: ProjectHelpers.getVerticalOptions(),
          },
        },
        {
          field: 'gender.display_value',
          headerName: 'Gender',
          headerClass: 'header-dropdown',
          sortable: true,
          editable: true,
          filter: true,
          filterParams: {
            defaultToNothingSelected: true,
            values: ProjectHelpers.getGenderOptions().map((option) => option?.value),
          },
          cellEditorParams: {
            key: 'gender',
          },
          context: {
            renderViewType: 'chip',
            renderEditType: 'single-select',
            dropdownSelections: ProjectHelpers.getGenderOptions(),
          },
        },
        {
          field: 'decision.display_value',
          headerName: 'Decision (Client)',
          headerClass: 'header-dropdown',
          sortable: true,
          editable: true,
          filter: true,
          filterParams: {
            defaultToNothingSelected: true,
            values: ProjectHelpers.getDecisionOptions().map((option) => option?.value),
          },
          cellEditorParams: {
            key: 'decision',
          },
          singleClickEdit: true,
          context: {
            renderViewType: 'decision',
            renderEditType: 'decision',
            dropdownSelections: ProjectHelpers.getDecisionOptions(),
          },
        },
        {
          field: 'client_notes.display_value',
          headerName: 'Client Feedback',
          headerClass: 'header-string',
          sortable: true,
          editable: true,
          filter: true,
          keyCreator: (params) => (params.value ? 'Yes' : 'No'),
          filterParams: {
            valueFormatter: (params) => params.value,
            defaultToNothingSelected: true,
            values: ['Yes', 'No'],
          },
          filterValueGetter: (params) => {
            return params.data.client_notes.display_value ? 'Yes' : 'No';
          },
          cellEditorParams: {
            key: 'client_notes',
            maxLength: 10000,
          },
          cellEditor: 'agLargeTextCellEditor',
          context: {},
        },
        {
          field: 'ethnicity.display_value',
          headerClass: 'header-dropdown',
          headerName: 'Ethnicity',
          sortable: true,
          editable: true,
          filter: true,
          filterParams: {
            defaultToNothingSelected: true,
            values: ProjectHelpers.getEthnicityOptions().map((option) => option?.value),
          },
          cellEditorParams: {
            key: 'ethnicity',
          },
          context: {
            renderViewType: 'chip',
            renderEditType: 'single-select',
            multiSelect: true,
            dropdownSelections: ProjectHelpers.getEthnicityOptions(),
          },
        },
        {
          field: 'content_rights.display_value',
          headerClass: 'header-dropdown',
          headerName: 'Rights',
          sortable: true,
          editable: true,
          filter: true,
          filterParams: {
            defaultToNothingSelected: true,
            values: ProjectHelpers.getRightsOptions().map((option) => option?.value),
          },
          cellEditorParams: {
            key: 'content_rights',
          },
          context: {
            renderViewType: 'chip',
            renderEditType: 'single-select',
            singleSelect: true,
            hideNoDate: true,
            dropdownSelections: ProjectHelpers.getRightsOptions(),
          },
        },
        {
          field: 'content_rights_expiration_date.display_value',
          headerName: 'Exp. Date',
          headerClass: 'header-date',
          singleClickEdit: true,
          sortable: true,
          editable: true,
          filter: true,
          filterParams: {
            defaultToNothingSelected: true,
            values: ['Yes', 'No'],
          },
          filterValueGetter: (params) => {
            return params.data.content_rights_expiration_date.display_value ? 'Yes' : 'No';
          },
          cellEditorParams: {
            key: 'content_rights_expiration_date',
          },
          context: {
            renderViewType: 'date',
            renderEditType: 'date',
          },
        },
        {
          field: 'payment_method.display_value',
          headerClass: 'header-dropdown',
          headerName: 'Pay by',
          sortable: true,
          editable: true,
          filter: true,
          filterParams: {
            defaultToNothingSelected: true,
            values: ProjectHelpers.getPayByOptions().map((option) => option?.value),
          },
          cellEditorParams: {
            key: 'payment_method',
          },
          context: {
            renderViewType: 'chip',
            renderEditType: 'single-select',
            multiSelect: false,
            dropdownSelections: ProjectHelpers.getPayByOptions(),
          },
        },
        {
          field: 'paypal_email.display_value',
          headerName: 'Paypal Email',
          headerClass: 'header-string',
          sortable: true,
          editable: true,
          keyCreator: (params) => (params.value ? 'Yes' : 'No'),
          filter: true,
          filterParams: {
            defaultToNothingSelected: true,
            valueFormatter: (params) => params.value,
            values: ['Yes', 'No'],
          },
          filterValueGetter: (params) => {
            return params.data.paypal_email.display_value ? 'Yes' : 'No';
          },
          cellEditorParams: {
            key: 'paypal_email',
          },
          cellRenderer: (params) => {
            return params.data?.paypal_email.display_value ? `<a href="mailto:${params.data.paypal_email.display_value}" target="_blank">${params.data.paypal_email.display_value}</a>` : '';
          },
          context: {},
        },
        {
          field: 'payee.display_value',
          headerClass: 'header-dropdown',
          headerName: 'Payee',
          sortable: true,
          editable: true,
          filter: true,
          filterParams: {
            defaultToNothingSelected: true,
            values: ProjectHelpers.getPayeeOptions().map((option) => option?.value),
          },
          cellEditorParams: {
            key: 'payee',
          },
          context: {
            renderViewType: 'chip',
            renderEditType: 'single-select',
            multiSelect: false,
            dropdownSelections: ProjectHelpers.getPayeeOptions(),
          },
        },
        {
          field: 'mgmt_co.display_value',
          headerName: 'Management Co.',
          headerClass: 'header-string',
          sortable: true,
          editable: true,
          keyCreator: (params) => (params.value ? 'Yes' : 'No'),
          filter: true,
          filterParams: {
            defaultToNothingSelected: true,
            valueFormatter: (params) => params.value,
            values: ['Yes', 'No'],
          },
          filterValueGetter: (params) => {
            return params.data.mgmt_co.display_value ? 'Yes' : 'No';
          },
          cellEditorParams: {
            key: 'mgmt_co',
          },
          cellRenderer: (params) => {
            return params.data?.mgmt_co.display_value ?? '';
          },
          context: {},
        },
        {
          field: 'mgr_name.display_value',
          headerName: 'Mgr Name.',
          headerClass: 'header-string',
          sortable: true,
          editable: true,
          keyCreator: (params) => (params.value ? 'Yes' : 'No'),
          filter: true,
          filterParams: {
            defaultToNothingSelected: true,
            valueFormatter: (params) => params.value,
            values: ['Yes', 'No'],
          },
          filterValueGetter: (params) => {
            return params.data.mgr_name.display_value ? 'Yes' : 'No';
          },
          cellEditorParams: {
            key: 'mgr_name',
          },
          cellRenderer: (params) => {
            return params.data?.mgr_name.display_value ?? '';
          },
          context: {},
        },
        {
          field: 'mgr_email.display_value',
          headerName: 'Mgr Email',
          headerClass: 'header-string',
          sortable: true,
          editable: true,
          keyCreator: (params) => (params.value ? 'Yes' : 'No'),
          filter: true,
          filterParams: {
            defaultToNothingSelected: true,
            valueFormatter: (params) => params.value,
            values: ['Yes', 'No'],
          },
          filterValueGetter: (params) => {
            return params.data.mgr_email.display_value ? 'Yes' : 'No';
          },
          cellEditorParams: {
            key: 'mgr_email',
          },
          cellRenderer: (params) => {
            return params.data?.mgr_email.display_value ? `<a href="mailto:${params.data.mgr_email.display_value}" target="_blank">${params.data.mgr_email.display_value}</a>` : '';
          },
          context: {},
        },
        ...customColumns,
      ];

      if (useProjectStore().isCreatorDetailsViewActive) {
        data.push(addColumn);
      }

      return data;
    },
  },
  actions: {
    setCreators(creators) {
      if (creators && creators.length > 0) {
        this.activeCreators = true;
        this.creators = this.generateCreatorsGridData(creators, this.entityMappings);
      } else {
        this.creators = ProjectHelpers.generateEmptyGridData('creators', 5, 0);
      }
    },

    setFilterOptions(column, options) {
      this.filterOptions.push({
        field: column.field,
        type: column.type,
        filterType: column?.context?.filterType,
        columnHeaderName: column.headerName,
        options: options,
        key: column?.cellEditorParams?.key,
        customColumn: column?.context?.customColumn,
        renderViewType: column?.context?.renderViewType,
      });
    },

    setFilteredSortedCreators(data) {
      this.filteredSortedCreators = data;
    },

    setFilteredActiveCreators(data) {
      this.filteredActiveCreators = data;
    },

    setViews(views) {
      if (views && views.length > 0) {
        // check if the views array contain a view name that is "Internal Sheet" and make that the first one in the array
        const internalSheetView = views.find((view) => view.name === 'Internal Sheet');
        if (internalSheetView) {
          views = views.filter((view) => view.name !== 'Internal Sheet');
          views.unshift(internalSheetView);
        }

        this.views = views;
      }
    },

    setActiveView(view) {
      this.activeView = view;
    },

    addCreatorToProject(creator) {
      if (creator) {
        const formattedCreator = this.generateCreatorsGridData([creator], this.entityMappings);

        if (this.activeCreators) {
          formattedCreator[0].id = this.creators.length + 1;
          this.creators = [...this.creators, formattedCreator[0]];
        } else {
          this.creators = formattedCreator;
          this.activeCreators = true;
        }
      }
    },

    async removeCreatorFromProject(creator) {
      try {
        useProjectStore().isGridUpdating = true;
        this.creators = this.creators.filter((c) => Number(c.creator_id) !== Number(creator.creator_id));

        if (this.creators.length === 0) {
          this.activeCreators = false;
          this.creators = ProjectHelpers.generateEmptyGridData('creators', 5, 0);
        }

        const res = await API.sendQuery('/graphql/project/creator', REMOVE_PROJECT_CREATOR_MUTATION, {
          project_id: Number(useProjectStore().currentProjectID),
          project_creator_id: Number(creator.creator_id),
        });

        if (res.data.data.removeCreator) {
          console.log('Creator removed successfully');
        }
      } catch (error) {
        console.error('Failed to remove creator from project:', error);
      } finally {
        useProjectStore().isGridUpdating = false;
      }
    },

    async setViewShared(projectViewID, shared = true) {
      let res = await API.sendQuery('/graphql/project', UPDATE_PROJECT_VIEWS_MUTATION, { id: projectViewID, shared: shared });
      if (res.data.data.updateView?.id) {
        this.activeView.shared = res.data.data.updateView.shared;
      }
    },

    async initProjectCreators(isLoading = true) {
      this.isLoadingCreators = isLoading;
      this.filterOptions = []; // Reset filter options for creators
      const accountID = Number(useProjectStore().activeAccount.id);
      const projectID = Number(useProjectStore().currentProjectID);

      const [entityMappings, creators, views] = await Promise.all([
        API.sendQuery('/graphql/project', GET_PROJECT_ENTITY_MAPPING_QUERY, { project_id: projectID, entity_name: ['project_creator'] }),
        API.sendQuery('/graphql/project', GET_PROJECT_CREATORS_QUERY, { project_id: projectID }),
      ]);

      if (entityMappings.data.data.entityMappings) {
        this.entityMappings = entityMappings.data.data.entityMappings;
      }

      if (creators.data.data.creators) {
        this.setCreators(creators.data.data.creators);
      }
      this.isLoadingCreators = false;
    },

    setTempSpreadsheetImportData(data) {
      this.tempUploadedData.originalCreators = data?.users;
      this.tempUploadedData.entity_mapping = data?.entity_mapping;
      this.tempUploadedData.creators = this.generateTempCreatorsFromSpreadsheet(data?.users);
    },

    updateColumn(entityMappingId, headerName) {
      const column = this.entityMappings.creators.find((column) => Number(column.id) === Number(entityMappingId));
      if (column) {
        // add more properties to update in the future...
        column.field = headerName;
      }
    },

    // Helper function for generating grid data (creators) rows based on the provided data.
    generateCreatorsGridData(creators = [], entityMappings = []) {
      if (creators && creators.length > 0) {
        return creators.map((creator, index) => {
          // pluck out the custom properties and build the creator object
          const customProperties = creator.custom_properties;
          const customPropertiesFields = {};
          const customEntityMappingsFields = {};

          if (customProperties && customProperties.length > 0) {
            customProperties.map((customProperty) => {
              const key = customProperty.key.replaceAll(' ', '').toLowerCase() + '_custom_property';

              let displayValue = customProperty.value.length > 1 ? customProperty.value : customProperty.value[0];

              if (customProperty.value[0] === 'true') {
                displayValue = true;
              }

              if (customProperty.value[0] === 'false') {
                displayValue = false;
              }

              return (customPropertiesFields[key] = {
                display_value: displayValue,
                endpoint: '/graphql/project/creator',
                query: UPDATE_PROJECT_CREATOR_MUTATION,
                key: key,
                originalKey: customProperty.key,
                custom_property: true,
                args: {
                  project_id: Number(useProjectStore().activeProject.id),
                  project_creator_id: Number(creator.id),
                },
              });
            });
          }

          // check for entity mappings and create creator objects (create new data objects for custom properties)
          entityMappings.map((mapping, index) => {
            const customEntityKey = `${mapping.field.replaceAll(' ', '').toLowerCase()}_custom_property`;
            // check if the custom entity key exists in the creator customPropertiesFields
            if (!customPropertiesFields[customEntityKey]) {
              return (customEntityMappingsFields[customEntityKey] = {
                display_value: creator.account_creator[customEntityKey] ?? '',
                endpoint: '/graphql/project/creator',
                query: UPDATE_PROJECT_CREATOR_MUTATION,
                key: customEntityKey,
                originalKey: mapping.field,
                custom_property: true,
                args: {
                  project_id: Number(useProjectStore().activeProject.id),
                  project_creator_id: Number(creator.id),
                },
              });
            }
          });

          const payment_method = creator?.account_creator?.custom_properties?.find((p) => p.key === 'payment_method')?.value?.[0];
          const paypal_email = creator?.account_creator?.custom_properties?.find((p) => p.key === 'paypal_email')?.value?.[0];
          const payee = creator?.account_creator?.custom_properties?.find((p) => p.key === 'payee')?.value?.[0];
          const mgmt_co = creator?.account_creator?.custom_properties?.find((p) => p.key === 'mgmt_co')?.value?.[0];
          const mgr_name = creator?.account_creator?.custom_properties?.find((p) => p.key === 'mgr_name')?.value?.[0];
          const mgr_email = creator?.account_creator?.custom_properties?.find((p) => p.key === 'mgr_email')?.value?.[0];

          return {
            id: index + 1,
            creator_id: Number(creator.id),
            creator_account_id: Number(creator.account_creator.id),
            customProperties: creator.custom_properties,
            payment_method: {
              display_value: payment_method ? payment_method : '',
              endpoint: '/graphql/account/creator',
              query: UPDATE_CREATOR_MUTATION,
              key: 'payment_method',
              value_type: 'string',
              custom_property: true,
              originalKey: 'payment_method',
              args: {
                account_id: Number(useProjectStore().activeAccount.id),
                account_creator_id: Number(creator.account_creator.id),
              },
            },
            mgr_email: {
              display_value: mgr_email ? mgr_email : '',
              endpoint: '/graphql/account/creator',
              query: UPDATE_CREATOR_MUTATION,
              key: 'mgr_email',
              value_type: 'string',
              custom_property: true,
              originalKey: 'mgr_email',
              args: {
                account_id: Number(useProjectStore().activeAccount.id),
                account_creator_id: Number(creator.account_creator.id),
              },
            },
            mgr_name: {
              display_value: mgr_name ? mgr_name : '',
              endpoint: '/graphql/account/creator',
              query: UPDATE_CREATOR_MUTATION,
              key: 'mgr_name',
              value_type: 'string',
              custom_property: true,
              originalKey: 'mgr_name',
              args: {
                account_id: Number(useProjectStore().activeAccount.id),
                account_creator_id: Number(creator.account_creator.id),
              },
            },
            mgmt_co: {
              display_value: mgmt_co ? mgmt_co : '',
              endpoint: '/graphql/account/creator',
              query: UPDATE_CREATOR_MUTATION,
              key: 'mgmt_co',
              value_type: 'string',
              custom_property: true,
              originalKey: 'mgmt_co',
              args: {
                account_id: Number(useProjectStore().activeAccount.id),
                account_creator_id: Number(creator.account_creator.id),
              },
            },
            paypal_email: {
              display_value: paypal_email ? paypal_email : '',
              endpoint: '/graphql/account/creator',
              query: UPDATE_CREATOR_MUTATION,
              key: 'paypal_email',
              value_type: 'string',
              custom_property: true,
              originalKey: 'paypal_email',
              args: {
                account_id: Number(useProjectStore().activeAccount.id),
                account_creator_id: Number(creator.account_creator.id),
              },
            },
            payee: {
              display_value: payee ? payee : '',
              endpoint: '/graphql/account/creator',
              query: UPDATE_CREATOR_MUTATION,
              key: 'payee',
              value_type: 'string',
              custom_property: true,
              originalKey: 'payee',
              args: {
                account_id: Number(useProjectStore().activeAccount.id),
                account_creator_id: Number(creator.account_creator.id),
              },
            },
            avatar: {
              display_value: creator.account_creator.avatar ?? '',
              endpoint: '/graphql/account/creator',
              query: UPDATE_CREATOR_MUTATION,
              key: 'avatar',
              value_type: 'string',
              args: {
                account_id: Number(useProjectStore().activeAccount.id),
                account_creator_id: Number(creator.account_creator.id),
              },
            },
            first_name: {
              display_value: creator.account_creator.first_name ?? '',
              endpoint: '/graphql/account/creator',
              query: UPDATE_CREATOR_MUTATION,
              key: 'first_name',
              value_type: 'string',
              args: {
                account_id: Number(useProjectStore().activeAccount.id),
                account_creator_id: Number(creator.account_creator.id),
              },
            },
            last_name: {
              display_value: creator.account_creator.last_name ?? '',
              endpoint: '/graphql/account/creator',
              query: UPDATE_CREATOR_MUTATION,
              key: 'last_name',
              value_type: 'string',
              args: {
                account_id: Number(useProjectStore().activeAccount.id),
                account_creator_id: Number(creator.account_creator.id),
              },
            },
            creator: {
              display_value: creator.account_creator ?? '',
              endpoint: '/graphql/account/creator',
              query: UPDATE_CREATOR_MUTATION,
              key: 'creator',
              value_type: 'string',
              args: {
                account_id: Number(useProjectStore().activeAccount.id),
                account_creator_id: Number(creator.account_creator.id),
              },
            },
            username: {
              display_value: creator.account_creator.username ?? '',
              endpoint: '/graphql/account/creator',
              query: UPDATE_CREATOR_MUTATION,
              key: 'username',
              value_type: 'string',
              args: {
                account_id: Number(useProjectStore().activeAccount.id),
                account_creator_id: Number(creator.account_creator.id),
              },
            },
            email: {
              display_value: creator.account_creator.email ?? '',
              endpoint: '/graphql/account/creator',
              query: UPDATE_CREATOR_MUTATION,
              key: 'email',
              value_type: 'string',
              args: {
                account_id: Number(useProjectStore().activeAccount.id),
                account_creator_id: Number(creator.account_creator.id),
              },
            },
            phone: {
              display_value: creator.account_creator.phone ?? '',
              endpoint: '/graphql/account/creator',
              query: UPDATE_CREATOR_MUTATION,
              key: 'phone',
              value_type: 'string',
              args: {
                account_id: Number(useProjectStore().activeAccount.id),
                account_creator_id: Number(creator.account_creator.id),
              },
            },
            gender: {
              display_value: creator.account_creator.gender ?? '',
              endpoint: '/graphql/account/creator',
              query: UPDATE_CREATOR_MUTATION,
              key: 'gender',
              value_type: 'array',
              args: {
                account_id: Number(useProjectStore().activeAccount.id),
                account_creator_id: Number(creator.account_creator.id),
              },
            },
            ethnicity: {
              display_value:
                creator.account_creator?.ethnicity.filter((item) => {
                  if (item !== '') {
                    return item;
                  }
                }) ?? [],
              endpoint: '/graphql/account/creator',
              query: UPDATE_CREATOR_MUTATION,
              key: 'ethnicity',
              value_type: 'array',
              args: {
                account_id: Number(useProjectStore().activeAccount.id),
                account_creator_id: Number(creator.account_creator.id),
              },
            },
            verticals: {
              display_value: creator.account_creator.verticals ?? [],
              endpoint: '/graphql/account/creator',
              query: UPDATE_CREATOR_MUTATION,
              key: 'verticals',
              value: creator.account_creator.verticals,
              value_type: 'array',
              args: {
                account_id: Number(useProjectStore().activeAccount.id),
                account_creator_id: Number(creator.account_creator.id),
              },
            },
            w9: {
              display_value: creator.account_creator.w9 ?? '',
              endpoint: '/graphql/account/creator',
              query: UPDATE_CREATOR_MUTATION,
              key: 'w9',
              value: creator.account_creator.w9,
              value_type: 'string',
              args: {
                account_id: Number(useProjectStore().activeAccount.id),
                account_creator_id: Number(creator.account_creator.id),
              },
            },
            w9_expiration_date: {
              display_value: creator.account_creator.w9_expiration_date ?? null,
              endpoint: '/graphql/account/creator',
              query: UPDATE_CREATOR_MUTATION,
              key: 'w9_expiration_date',
              value: creator.account_creator.w9_expiration_date,
              value_type: 'dateTime',
              args: {
                account_id: Number(useProjectStore().activeAccount.id),
                account_creator_id: Number(creator.account_creator.id),
              },
            },
            deliverables: {
              display_value: creator?.deliverables ? ProjectHelpers.formatDeliverables(creator.deliverables) : [],
              // endpoint: '/graphql/project/deliverable',
              // query: CREATE_PROJECT_DELIVERABLE_MUTATION,
              // key: 'name',
              // value: creator?.deliverables,
              // args: {
              //   project_id: Number(useProjectStore().currentProjectID),
              //   project_creator_id: Number(creator.id)
              // }
            },
            documents: {
              display_value: creator?.documents ? creator.documents : [],
            },
            products: {
              display_value: creator?.products ? ProjectHelpers.formatProducts(creator.products) : [],
              // display_value: creator?.products ? creator.products.map((product) => Number(product.id)) : [],
              endpoint: '/graphql/project/creator',
              query: PROJECT_CREATOR_SYNC_PRODUCTS_MUTATION,
              key: 'product_ids',
              value: creator?.products ? ProjectHelpers.formatProducts(creator.products) : [],
              value_type: 'array',
              args: {
                project_id: Number(useProjectStore().currentProjectID),
                project_creator_id: Number(creator.id),
              },
            },
            briefing_files: {
              display_value: creator?.briefing_files ? ProjectHelpers.formatBriefingFiles(creator.briefing_files).filter((f) => !f?.deliverable_id) : [],
              // display_value: creator?.products ? creator.products.map((product) => Number(product.id)) : [],
              endpoint: '/graphql/project/creator',
              query: PROJECT_CREATOR_SYNC_BRIEFINGFILES_MUTATION,
              key: 'upload_ids',
              value: creator?.briefing_files ? ProjectHelpers.formatBriefingFiles(creator.briefing_files).filter((f) => !f?.deliverable_id) : [],
              value_type: 'array',
              args: {
                project_id: Number(useProjectStore().currentProjectID),
                project_creator_id: Number(creator.id),
              },
            },
            social_channels: {
              display_value: creator?.social_channels,
            },
            savings: {
              display_value: creator?.initial_quote && creator.contract?.contract_value ? creator.initial_quote - creator.contract.contract_value : '',
            },
            indeminity: {
              display_value: creator.contract?.indeminity ?? '',
              endpoint: '/graphql/project/creator',
              query: UPDATE_CREATOR_CONTRACT_MUTATION,
              key: 'indeminity',
              value_type: 'boolean',
              args: {
                project_id: Number(useProjectStore().currentProjectID),
                project_creator_id: Number(creator.id),
              },
            },
            whitelisting: {
              display_value: creator.contract?.whitelisting ?? '',
              endpoint: '/graphql/project/creator',
              query: UPDATE_CREATOR_CONTRACT_MUTATION,
              key: 'whitelisting',
              value_type: 'boolean',
              args: {
                project_id: Number(useProjectStore().currentProjectID),
                project_creator_id: Number(creator.id),
              },
            },
            whitelisting_expiration: {
              display_value: creator.contract?.whitelisting_expiration ?? '',
              endpoint: '/graphql/project/creator',
              query: UPDATE_CREATOR_CONTRACT_MUTATION,
              key: 'whitelisting_expiration',
              value_type: 'string',
              args: {
                project_id: Number(useProjectStore().currentProjectID),
                project_creator_id: Number(creator.id),
              },
            },
            content_rights_expiration_date: {
              display_value: creator.contract?.content_rights_expiration_date ?? null,
              endpoint: '/graphql/project/creator',
              query: UPDATE_CREATOR_CONTRACT_MUTATION,
              key: 'content_rights_expiration_date',
              value_type: 'date',
              args: {
                project_id: Number(useProjectStore().currentProjectID),
                project_creator_id: Number(creator.id),
              },
            },
            ecom_asset_ownership: {
              display_value: creator.contract?.ecom_asset_ownership ?? '',
              endpoint: '/graphql/project/creator',
              query: UPDATE_CREATOR_CONTRACT_MUTATION,
              key: 'ecom_asset_ownership',
              value_type: 'string',
              args: {
                project_id: Number(useProjectStore().currentProjectID),
                project_creator_id: Number(creator.id),
              },
            },
            ecom_asset_expiration_date: {
              display_value: creator.contract?.ecom_asset_expiration_date ?? null,
              endpoint: '/graphql/project/creator',
              query: UPDATE_CREATOR_CONTRACT_MUTATION,
              key: 'ecom_asset_expiration_date',
              value_type: 'datetime',
              args: {
                project_id: Number(useProjectStore().currentProjectID),
                project_creator_id: Number(creator.id),
              },
            },
            content_ownership: {
              display_value: creator.contract?.content_ownership ?? '',
              endpoint: '/graphql/project/creator',
              query: UPDATE_CREATOR_CONTRACT_MUTATION,
              key: 'content_ownership',
              value_type: 'string',
              args: {
                project_id: Number(useProjectStore().currentProjectID),
                project_creator_id: Number(creator.id),
              },
            },
            kill_fee: {
              display_value: creator.contract?.kill_fee ? Number(creator.contract?.kill_fee) : '',
              endpoint: '/graphql/project/creator',
              query: UPDATE_CREATOR_CONTRACT_MUTATION,
              key: 'kill_fee',
              value_type: 'Float',
              args: {
                project_id: Number(useProjectStore().currentProjectID),
                project_creator_id: Number(creator.id),
              },
            },
            contract_notes: {
              display_value: creator.contract?.notes ?? '',
              endpoint: '/graphql/project/creator',
              query: UPDATE_CREATOR_CONTRACT_MUTATION,
              key: 'contract_notes',
              value_type: 'string',
              args: {
                project_id: Number(useProjectStore().currentProjectID),
                project_creator_id: Number(creator.id),
              },
            },
            contract_value: {
              display_value: creator.contract?.contract_value ? parseFloat(creator.contract.contract_value) : '',
              endpoint: '/graphql/project/creator',
              query: UPDATE_CREATOR_CONTRACT_MUTATION,
              key: 'contract_value',
              value_type: 'Float',
              args: {
                project_id: Number(useProjectStore().currentProjectID),
                project_creator_id: Number(creator.id),
              },
            },
            contract_status: {
              display_value: creator.contract?.status ?? '',
              endpoint: '/graphql/project/creator',
              query: UPDATE_CREATOR_CONTRACT_MUTATION,
              key: 'status',
              value_type: 'string',
              args: {
                project_id: Number(useProjectStore().currentProjectID),
                project_creator_id: Number(creator.id),
              },
            },
            content_rights: {
              display_value: creator.contract?.content_rights ?? '',
              endpoint: '/graphql/project/creator',
              query: UPDATE_CREATOR_CONTRACT_MUTATION,
              key: 'content_rights',
              value_type: 'string',
              args: {
                project_id: Number(useProjectStore().currentProjectID),
                project_creator_id: Number(creator.id),
              },
            },
            favorite: {
              display_value: creator.favorite === null ? '' : creator.favorite,
              endpoint: '/graphql/project/creator',
              query: UPDATE_PROJECT_CREATOR_MUTATION,
              key: 'favorite',
              value_type: 'boolean',
              args: {
                project_id: Number(useProjectStore().currentProjectID),
                project_creator_id: Number(creator.id),
              },
            },
            confirmed: {
              display_value: creator?.confirmed,
              endpoint: '/graphql/project/creator',
              query: UPDATE_PROJECT_CREATOR_MUTATION,
              key: 'confirmed',
              value_type: 'boolean',
              args: {
                project_id: Number(useProjectStore().currentProjectID),
                project_creator_id: Number(creator.id),
              },
            },
            initial_quote: {
              display_value: creator?.initial_quote ? parseFloat(creator.initial_quote) : '',
              endpoint: '/graphql/project/creator',
              query: UPDATE_PROJECT_CREATOR_MUTATION,
              key: 'initial_quote',
              value_type: 'Float',
              args: {
                project_id: Number(useProjectStore().currentProjectID),
                project_creator_id: Number(creator.id),
              },
            },
            notes: {
              display_value: creator?.notes ?? '',
              endpoint: '/graphql/project/creator',
              query: UPDATE_PROJECT_CREATOR_MUTATION,
              key: 'notes',
              value_type: 'text',
              args: {
                project_id: Number(useProjectStore().currentProjectID),
                project_creator_id: Number(creator.id),
              },
            },
            client_notes: {
              display_value: creator?.client_notes ?? '',
              endpoint: '/graphql/project/creator',
              query: UPDATE_PROJECT_CREATOR_MUTATION,
              key: 'client_notes',
              value_type: 'text',
              args: {
                project_id: Number(useProjectStore().currentProjectID),
                project_creator_id: Number(creator.id),
              },
            },
            decision: {
              display_value: creator?.decision === null ? '' : creator.decision,
              endpoint: '/graphql/project/creator',
              query: UPDATE_PROJECT_CREATOR_MUTATION,
              key: 'decision',
              value_type: 'boolean',
              args: {
                project_id: Number(useProjectStore().currentProjectID),
                project_creator_id: Number(creator.id),
              },
            },
            youtube_account: {
              display_value: creator?.social_channels
                ? (creator?.social_channels.filter((element) => {
                    return element.type === 'YouTube';
                  }) ?? [])
                : [],
              value: creator?.social_channels,
              endpoint: '/graphql/account/creator',
              key: 'youtube_account',
              originalKey: 'YT Account',
              value_type: 'array',
              args: null,
            },
            youtube_reach: {
              // find custom property value that matches the 'YT Reach' key from the custom properties array
              display_value: creator?.youtube_reach && creator?.youtube_reach !== 0 && creator?.youtube_reach !== '0' ? creator?.youtube_reach : '',
              endpoint: '/graphql/project/creator',
              query: UPDATE_PROJECT_CREATOR_MUTATION,
              key: 'youtube_reach',
              originalKey: 'YT Reach',
              value_type: 'string',
              args: {
                project_id: Number(useProjectStore().currentProjectID),
                project_creator_id: Number(creator.id),
              },
            },
            youtube_impressions: {
              // find custom property value that matches the 'YT Impressions' key from the custom properties array
              display_value: creator?.youtube_impressions && creator?.youtube_impressions !== 0 && creator?.youtube_impressions !== '0' ? creator?.youtube_impressions : '',
              endpoint: '/graphql/project/creator',
              query: UPDATE_PROJECT_CREATOR_MUTATION,
              key: 'youtube_impressions',
              originalKey: 'YT Impressions',
              value_type: 'string',
              args: {
                project_id: Number(useProjectStore().currentProjectID),
                project_creator_id: Number(creator.id),
              },
            },
            youtube_engagement_rate: {
              // find custom property value that matches the 'YT Estimated Reach' key from the custom properties array
              display_value: creator?.youtube_engagement_rate && creator?.youtube_engagement_rate !== 0 && creator?.youtube_engagement_rate !== '0' ? creator?.youtube_engagement_rate : '',
              endpoint: '/graphql/project/creator',
              query: UPDATE_PROJECT_CREATOR_MUTATION,
              key: 'youtube_engagement_rate',
              originalKey: 'YT ER',
              value_type: 'string',
              args: {
                project_id: Number(useProjectStore().currentProjectID),
                project_creator_id: Number(creator.id),
              },
            },
            tiktok_reach: {
              // find custom property value that matches the 'TikTok Reach' key from the custom properties array
              display_value: creator?.tiktok_reach && creator?.tiktok_reach !== 0 && creator?.tiktok_reach !== '0' ? creator?.tiktok_reach : '',
              endpoint: '/graphql/project/creator',
              query: UPDATE_PROJECT_CREATOR_MUTATION,
              key: 'tiktok_reach',
              originalKey: 'TT Reach',
              value_type: 'string',
              args: {
                project_id: Number(useProjectStore().currentProjectID),
                project_creator_id: Number(creator.id),
              },
            },
            tiktok_impressions: {
              // find custom property value that matches the 'TikTok Impressions' key from the custom properties array
              display_value: creator?.tiktok_impressions && creator?.tiktok_impressions !== 0 && creator?.tiktok_impressions !== '0' ? creator?.tiktok_impressions : '',
              endpoint: '/graphql/project/creator',
              query: UPDATE_PROJECT_CREATOR_MUTATION,
              key: 'tiktok_impressions',
              originalKey: 'TT Impressions',
              value_type: 'string',
              args: {
                project_id: Number(useProjectStore().currentProjectID),
                project_creator_id: Number(creator.id),
              },
            },
            tiktok_engagement_rate: {
              // find custom property value that matches the 'TikTok ER' key from the custom properties array
              display_value: creator?.tiktok_engagement_rate && creator?.tiktok_engagement_rate !== 0 && creator?.tiktok_engagement_rate !== '0' ? creator?.tiktok_engagement_rate : '',
              endpoint: '/graphql/project/creator',
              query: UPDATE_PROJECT_CREATOR_MUTATION,
              key: 'tiktok_engagement_rate',
              originalKey: 'TT ER',
              value_type: 'string',
              args: {
                project_id: Number(useProjectStore().currentProjectID),
                project_creator_id: Number(creator.id),
              },
            },
            tiktok_account: {
              display_value: creator?.social_channels
                ? (creator.social_channels.filter((element) => {
                    return element.type === 'TikTok';
                  }) ?? [])
                : [],
              value: creator?.social_channels,
              endpoint: '/graphql/account/creator',
              key: 'tiktok_account',
              originalKey: 'TT Account',
              value_type: 'array',
              args: null,
            },
            instagram_reach: {
              // find custom property value that matches the 'Instagram Reach' key from the custom properties array
              display_value: creator?.instagram_reach && creator?.instagram_reach !== 0 && creator?.instagram_reach !== '0' ? creator?.instagram_reach : '',
              endpoint: '/graphql/project/creator',
              query: UPDATE_PROJECT_CREATOR_MUTATION,
              key: 'instagram_reach',
              originalKey: 'IG Reach',
              value_type: 'string',
              args: {
                project_id: Number(useProjectStore().currentProjectID),
                project_creator_id: Number(creator.id),
              },
            },
            instagram_impressions: {
              // find custom property value that matches the 'IG Impressions' key from the custom properties array
              display_value: creator?.instagram_impressions && creator?.instagram_impressions !== 0 && creator?.instagram_impressions !== '0' ? creator?.instagram_impressions : '',
              endpoint: '/graphql/project/creator',
              query: UPDATE_PROJECT_CREATOR_MUTATION,
              key: 'instagram_impressions',
              originalKey: 'IG Impressions',
              value_type: 'string',
              args: {
                project_id: Number(useProjectStore().currentProjectID),
                project_creator_id: Number(creator.id),
              },
            },
            instagram_engagement_rate: {
              // find custom property value that matches the 'IG ER' key from the custom properties array
              display_value:
                creator?.instagram_engagement_rate && creator?.instagram_engagement_rate !== 0 && creator?.instagram_engagement_rate !== '0' ? creator?.instagram_engagement_rate : '',
              endpoint: '/graphql/project/creator',
              query: UPDATE_PROJECT_CREATOR_MUTATION,
              key: 'instagram_engagement_rate',
              originalKey: 'IG ER',
              value_type: 'string',
              args: {
                project_id: Number(useProjectStore().currentProjectID),
                project_creator_id: Number(creator.id),
              },
            },
            instagram_account: {
              display_value: creator?.social_channels
                ? (creator.social_channels.filter((element) => {
                    return element.type === 'Instagram';
                  }) ?? [])
                : [],
              value: creator?.social_channels,
              endpoint: '/graphql/account/creator',
              key: 'instagram_account',
              originalKey: 'IG Account',
              value_type: 'array',
              args: null,
            },
            example_content: {
              display_value: creator?.example_content ?? '',
              endpoint: '/graphql/project/creator',
              query: UPDATE_PROJECT_CREATOR_MUTATION,
              key: 'example_content',
              value_type: 'string',
              args: {
                project_id: Number(useProjectStore().activeProject.id),
                project_creator_id: Number(creator.id),
              },
            },
            invite: {
              display_value: creator?.invite ?? '',
            },
            // total_reach: {
            //   display_value: Helpers.formatNumberWithSuffix(
            //     Number(creator?.instagram_reach_custom_property?.display_value) +
            //       Number(creator?.tiktok_reach_custom_property?.display_value) +
            //       Number(creator?.youtube_reach_custom_property?.display_value)
            //   )
            // },
            ...customPropertiesFields,
            ...customEntityMappingsFields,
          };
        });
      } else {
        return [];
      }
    },

    generateTempCreatorsFromSpreadsheet(creators) {
      const lockedCustomColumns = [];
      let data = [];
      const customPropertiesFields = {};

      if (creators && creators.length > 0) {
        data = creators.map((creator, index) => {
          if (creator.custom_properties) {
            // pluck out the custom properties
            creator.custom_properties.map((customProperty) => {
              const key = customProperty.key.replaceAll(' ', '').toLowerCase() + '_custom_property';
              // lock these custom properties from editing in grid preview
              lockedCustomColumns.push(key);
              return (customPropertiesFields[key] = {
                display_value: customProperty.value[0],
              });
            });
          }
          return {
            id: index + 1,
            user_id: creator.id,
            creator: {
              display_value: {
                first_name: creator.first_name ?? '',
                last_name: creator.last_name ?? '',
                avatar: creator.avatar ?? '',
              },
            },
            avatar: {
              display_value: creator.avatar ?? '',
            },
            // first_name: {
            //   display_value: creator.first_name ?? ''
            // },
            // last_name: {
            //   display_value: creator.last_name ?? ''
            // },
            // username: {
            //   display_value: creator.username ?? ''
            // },
            // email: {
            //   display_value: creator.email ?? ''
            // },
            // phone: {
            //   display_value: creator.phone ?? ''
            // },
            // gender: {
            //   display_value: creator.gender ?? ''
            // },
            // ethnicity: {
            //   display_value: creator.ethnicity ?? ''
            // },
            // creator_confirm: {
            //   display_value: creator.confirmed ?? ''
            // },
            // favorite: {
            //   display_value: creator.favorite ?? ''
            // },
            // deliverable_count: {
            //   display_value: creator.deliverable_count ?? ''
            // },
            // initial_quote: {
            //   display_value: creator.initial_quote ?? ''
            // },
            social_channels: {
              display_value: creator.social_channels ?? [],
            },
            //deliverables: {
            //  display_value: creator.deliverables
            //    ? creator.deliverables.map((deliverable) => {
            //        return deliverable.name;
            //      })
            //    : []
            //},
            //amazon_storefront_link: {
            //  display_value: creator?.storefront?.url ?? ''
            //},
            //amazon_audience_affinity: {
            //  display_value: creator?.storefront?.audience_affinity ?? ''
            //},
            //address: {
            //  display_value: creator?.address ?? ''
            //},
            products: {
              display_value: creator?.products ?? [],
            },
            //example_content: {
            //  display_value: creator?.example_content ?? ''
            //},
            //fun_facts: {
            //  display_value: creator?.notes ?? ''
            //},
            //content_rights: {
            //  display_value: creator?.contract?.content_rights ?? ''
            //},
            //content_rights_expiration_date: {
            //  display_value: creator?.contract?.content_rights_expiration_date ?? ''
            //},
            //ecom_asset_rights: {
            //  display_value: creator?.contract.ecom_asset_rights ?? ''
            //},
            //ecom_rights_expiration_date: {
            //  display_value: creator?.contract.ecom_rights_expiration_date ?? ''
            //},
            //contract_status: {
            //  display_value: creator?.contract.contract_status ?? ''
            //},
            //contract_value: {
            //  display_value: creator?.contract.contract_value ?? ''
            //},
            //storefront: {
            //  display_value: creator?.storefront ?? ''
            //},
            //tiktok_reach: {
            //  display_value: creator?.tiktok_reach ?? ''
            //},
            //tiktok_impressions: {
            //  display_value: creator?.tiktok_impressions ?? ''
            //},
            //tiktok_engagement_rate: {
            //  display_value: creator?.tiktok_engagement_rate ?? ''
            //},
            //instagram_reach: {
            //  display_value: creator?.instagram_reach ?? ''
            //},
            //instagram_impressions: {
            //  display_value: creator?.instagram_impressions ?? ''
            //},
            //instagram_engagement_rate: {
            //  display_value: creator?.instagram_engagement_rate ?? ''
            //},
            //youtube_reach: {
            //  display_value: creator?.youtube_reach ?? ''
            //},
            //youtube_impressions: {
            //  display_value: creator?.youtube_impressions ?? ''
            //},
            //youtube_engagement_rate: {
            //  display_value: creator?.youtube_engagement_rate ?? ''
            //},
            errors: {
              display_value: creator?.errors ?? [],
            },
            //...customPropertiesFields
          };
        });
      }
      // set the locked custom columns in the store
      this.tempLockedCustomColumns = lockedCustomColumns;
      console.log('data', data);
      return data;
    },
  },
});
